import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  MoreVert,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useOrders } from "../../context/OrderContext";
import { useCart } from "../../context/CartContext";

export default function Orders() {
  const { orders, updateOrderDate, cancelOrder } = useOrders();
  const { addItem } = useCart();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleRow = (orderId) => {
    setOpenRow(openRow === orderId ? null : orderId);
  };

  const handleEditDate = () => {
    setOpenEditDialog(true);
    setSelectedDate(dayjs(selectedOrder?.orderedDate));
    handleMenuClose();
  };

  const handleCancelOrder = () => {
    setOpenCancelDialog(true);
    handleMenuClose();
  };

  const handleCopyToCart = () => {
    if (!selectedOrder) return;

    const items = selectedOrder.products ?? [];

    items.forEach((it) => {
      const productSku = String(it.sku);
      const variant = String(it.variant ?? "").trim();

      if (!productSku) return;

      const cartSku = variant ? `${productSku}-${variant}` : productSku;

      addItem(
        {
          sku: cartSku,
          productSku,
          productName: it.name ?? "",
          variantName: variant,
          price: Number(it.price ?? 0),
          image: it.image ?? null,
        },
        Number(it.quantity ?? 1),
      );
    });

    handleMenuClose();
  };

  const confirmEditDate = async () => {
    try {
      await updateOrderDate(selectedOrder.id, selectedDate.toDate());
    } catch (err) {
      console.error("❌ Error updating order date", err);
    }
    setOpenEditDialog(false);
  };

  const confirmCancelOrder = async () => {
    try {
      await cancelOrder(selectedOrder.id);
    } catch (err) {
      console.error("❌ Error canceling order", err);
    }
    setOpenCancelDialog(false);
  };

  return (
  <Box sx={{ width: "100%", p: { xs: 1.5, sm: 2.5, md: 3 } }}>
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 860 }}>
          {/* ===================== HEAD ===================== */}
          <TableHead sx={{ backgroundColor: "primary.main" }}>
            <TableRow>
              <TableCell />

              {/* Desktop columns */}
              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", sm: "table-cell" },
                }}
              >
                ID
              </TableCell>

              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", sm: "table-cell" },
                }}
              >
                Date
              </TableCell>

              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", md: "table-cell" }, // hide on small tablets
                }}
              >
                Last Modified
              </TableCell>

              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", sm: "table-cell" },
                }}
              >
                Items
              </TableCell>

              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", sm: "table-cell" },
                }}
              >
                Status
              </TableCell>

              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "none", sm: "table-cell" },
                }}
                align="center"
              >
                Total
              </TableCell>

              {/* Mobile/Tablet compact column */}
              <TableCell
                sx={{
                  color: "white",
                  fontWeight: 800,
                  display: { xs: "table-cell", sm: "none" },
                }}
              >
                Order
              </TableCell>

              <TableCell sx={{ color: "white", fontWeight: 800 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          {/* ===================== BODY ===================== */}
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 8} align="center">
                  <Typography sx={{ py: 4, color: "text.secondary" }}>
                    No hay órdenes disponibles
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <React.Fragment key={order.id}>
                  {/* ===================== MAIN ROW ===================== */}
                  <TableRow
                    hover
                    sx={{
                      "& td": { verticalAlign: "middle" },
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    {/* Expand */}
                    <TableCell sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(order.id)}
                        aria-label={`Toggle order ${order.id}`}
                      >
                        {openRow === order.id ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>

                    {/* DESKTOP CELLS */}
                    <TableCell
                      sx={{
                        display: { xs: "none", sm: "table-cell" },
                        fontWeight: 900,
                      }}
                    >
                      {order.id}
                    </TableCell>

                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      {new Date(order.orderedDate).toLocaleString(undefined, {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>

                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      {order.modificationDate
                        ? new Date(order.modificationDate).toLocaleString(
                            undefined,
                            {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )
                        : "-"}
                    </TableCell>

                    {/* Items preview (DESKTOP) - use img for clarity */}
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {(order.products ?? []).slice(0, 3).map((p, i) => (
                          <Box
                            key={`${p.sku}-${i}`}
                            component="img"
                            src={p.image || ""}
                            alt={p.name}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            sx={{
                              width: 44,
                              height: 44,
                              minWidth: 44,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              objectFit: "cover",
                              bgcolor: "grey.100",
                              display: "block",
                            }}
                            onError={(e) => {
                              (e.currentTarget).src = "";
                            }}
                          />
                        ))}
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {order.products?.length || 0}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <Chip
                        label={order.status}
                        color={
                          order.status === "CREATED"
                            ? "success"
                            : order.status === "CANCELED"
                              ? "error"
                              : order.status === "PENDING"
                                ? "warning"
                                : "default"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      />
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Typography fontWeight={900}>
                        ${Number(order.total ?? 0).toFixed(2)}
                      </Typography>
                    </TableCell>

                    {/* MOBILE/TABLET COMPACT CELL */}
                    <TableCell sx={{ display: { xs: "table-cell", sm: "none" } }}>
                      <Stack spacing={0.9}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography fontWeight={900}>
                            Order #{order.id}
                          </Typography>
                          <Typography fontWeight={900}>
                            ${Number(order.total ?? 0).toFixed(2)}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap" }}
                        >
                          <Chip
                            label={order.status}
                            color={
                              order.status === "CREATED"
                                ? "success"
                                : order.status === "CANCELED"
                                  ? "error"
                                  : order.status === "PENDING"
                                    ? "warning"
                                    : "default"
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              fontWeight: 900,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          />
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {new Date(order.orderedDate).toLocaleString(undefined, {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ overflowX: "auto", pb: 0.25 }}
                        >
                          {(order.products ?? []).slice(0, 6).map((p, i) => (
                            <Box
                              key={`${p.sku}-${i}`}
                              component="img"
                              src={p.image || ""}
                              alt={p.name}
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              sx={{
                                width: 56,
                                height: 56,
                                minWidth: 56,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                objectFit: "cover",
                                bgcolor: "grey.100",
                                display: "block",
                              }}
                              onError={(e) => {
                                (e.currentTarget).src = "";
                              }}
                            />
                          ))}
                          <Typography variant="caption" sx={{ color: "text.secondary", pr: 1 }}>
                            {order.products?.length || 0} item(s)
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ width: 64 }}>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, order)}
                        aria-label={`Order ${order.id} actions`}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* ===================== COLLAPSE ===================== */}
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 8} sx={{ p: 0 }}>
                      <Collapse in={openRow === order.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography variant="h6" gutterBottom align="center">
                            Products
                          </Typography>

                          {/* Desktop/Tablet: table; Mobile: cards */}
                          {isMobile ? (
                            <Stack spacing={1}>
                              {(order.products ?? []).map((p, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "background.paper",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={p.image || ""}
                                    alt={p.name}
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                    sx={{
                                      width: 72,
                                      height: 72,
                                      minWidth: 72,
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      objectFit: "cover",
                                      bgcolor: "grey.100",
                                      display: "block",
                                    }}
                                    onError={(e) => {
                                      (e.currentTarget).src = "";
                                    }}
                                  />

                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography fontWeight={900} sx={{ lineHeight: 1.15 }}>
                                      {p.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                      SKU: {p.sku} • {p.variant}
                                    </Typography>

                                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                                      <Chip
                                        label={`Qty: ${p.quantity}`}
                                        size="small"
                                        sx={{ fontWeight: 800 }}
                                      />
                                      <Chip
                                        label={`$${Number(p.total ?? 0).toFixed(2)}`}
                                        size="small"
                                        sx={{ fontWeight: 900 }}
                                      />
                                    </Stack>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Table
                              size="small"
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                overflow: "hidden",
                                "& thead th": {
                                  fontWeight: 900,
                                  backgroundColor: "grey.100",
                                },
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Image</TableCell>
                                  <TableCell>Product</TableCell>
                                  <TableCell align="center">Quantity</TableCell>
                                  <TableCell align="center">Price</TableCell>
                                  <TableCell align="center">Total</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {(order.products ?? []).map((p, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell sx={{ width: 84 }}>
                                      <Box
                                        component="img"
                                        src={p.image || ""}
                                        alt={p.name}
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          minWidth: 56,
                                          borderRadius: 2,
                                          border: "1px solid",
                                          borderColor: "divider",
                                          objectFit: "cover",
                                          bgcolor: "grey.100",
                                          display: "block",
                                        }}
                                        onError={(e) => {
                                          (e.currentTarget).src = "";
                                        }}
                                      />
                                    </TableCell>

                                    <TableCell>
                                      <Typography fontWeight={900} sx={{ lineHeight: 1.2 }}>
                                        {p.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        SKU: {p.sku} • {p.variant}
                                      </Typography>
                                    </TableCell>

                                    <TableCell align="center">{p.quantity}</TableCell>

                                    <TableCell align="center">
                                      ${Number(p.price ?? 0).toFixed(2)}
                                    </TableCell>

                                    <TableCell align="center">
                                      <Typography fontWeight={900}>
                                        ${Number(p.total ?? 0).toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </TableContainer>

    {/* ===================== MENU ===================== */}
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
      <MenuItem onClick={handleEditDate}>Edit Date</MenuItem>
      <MenuItem onClick={handleCancelOrder}>Cancel Order</MenuItem>
      <MenuItem onClick={handleCopyToCart}>Copy items to cart</MenuItem>
    </Menu>

    {/* ===================== EDIT DATE DIALOG ===================== */}
    <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
      <DialogTitle>Edit Order Date</DialogTitle>
      <DialogContent>
        <DatePicker
          label="Select new date"
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
        <Button onClick={confirmEditDate} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>

    {/* ===================== CANCEL DIALOG ===================== */}
    <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
      <DialogTitle>Cancel Order</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to cancel order #{selectedOrder?.id}?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenCancelDialog(false)}>No</Button>
        <Button onClick={confirmCancelOrder} color="error" variant="contained">
          Yes, Cancel
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
);

}
